import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RequestReasonService } from "@/services/request-reason.service";
import type { TRequestStatusReason, TRequestStatusReasonGroup } from "@/types/intake";

/**
 * Just the slice of the root store this one uses.
 *
 * Typing the constructor arg structurally instead of importing `RootStore`
 * keeps this module out of the `store -> root.store -> store` import cycle that
 * `check:deps` ratchets (and whose baseline is meant to shrink, not grow).
 * `RootStore` satisfies it structurally, so the call site is unchanged.
 */
interface RequestReasonServiceHost {
  requestReasonService: RequestReasonService;
}

/**
 * The workspace's request-reason lists as the Settings editor sees them (#4286)
 * — including retired reasons, which is the difference from the reviewer's
 * picker.
 *
 * Separate from `useRequestReasons` on purpose. That hook deliberately performs
 * uncached reads so an open decision dialog can't offer a reason an admin just
 * retired; this store caches per group, because each editor needs a list it can
 * mutate optimistically and re-render from. The two read the same endpoint with
 * different needs, and collapsing them would force one of those needs to lose.
 *
 * Writes are optimistic with snapshot rollback, per the design system's
 * interaction rule — the exception being create, which needs the server-assigned
 * id and `order` before it can render a real row.
 */
export class RequestReasonsStore {
  loadingKeys: Set<string> = new Set();
  fetchError: Error | null = null;
  isMutating = false;
  mutatingReasonId: string | null = null;
  reasonsByKey: Map<string, TRequestStatusReason[]> = new Map();

  private rootStore: RequestReasonServiceHost;

  constructor(rootStore: RequestReasonServiceHost) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        reasonsByKey: observable,
        loadingKeys: observable,
      } as never,
      { autoBind: true },
    );
  }

  private cacheKey(workspaceSlug: string, group: TRequestStatusReasonGroup): string {
    return `${workspaceSlug}:${group}`;
  }

  getReasons(workspaceSlug: string, group: TRequestStatusReasonGroup): TRequestStatusReason[] {
    return this.reasonsByKey.get(this.cacheKey(workspaceSlug, group)) ?? [];
  }

  isLoading(workspaceSlug: string, group: TRequestStatusReasonGroup): boolean {
    return this.loadingKeys.has(this.cacheKey(workspaceSlug, group));
  }

  /** Whether this group's list has been fetched at least once. The Statuses
   *  card's count badge gates on this: the fetch is deferred until the section
   *  expands, and rendering a literal 0 before any fetch would read as "no
   *  reasons configured" on a workspace that has a full list. */
  hasFetched(workspaceSlug: string, group: TRequestStatusReasonGroup): boolean {
    return this.reasonsByKey.has(this.cacheKey(workspaceSlug, group));
  }

  private setReasons(
    workspaceSlug: string,
    group: TRequestStatusReasonGroup,
    reasons: TRequestStatusReason[],
  ): void {
    this.reasonsByKey.set(this.cacheKey(workspaceSlug, group), reasons);
  }

  async fetchReasons(
    workspaceSlug: string,
    group: TRequestStatusReasonGroup,
  ): Promise<TRequestStatusReason[]> {
    const key = this.cacheKey(workspaceSlug, group);
    runInAction(() => {
      this.loadingKeys.add(key);
      this.fetchError = null;
    });
    try {
      const reasons = await this.rootStore.requestReasonService.list(workspaceSlug, group, {
        includeInactive: true,
      });
      runInAction(() => this.setReasons(workspaceSlug, group, reasons));
      return reasons;
    } catch (error) {
      console.error("[request-reasons] fetch failed", { workspaceSlug, group, error });
      runInAction(() => {
        this.fetchError = error as Error;
      });
      return [];
    } finally {
      runInAction(() => {
        this.loadingKeys.delete(key);
      });
    }
  }

  /** Not optimistic: the server assigns the id and appends the `order`, and a
   *  placeholder row would have neither to render or drag. */
  async createReason(
    workspaceSlug: string,
    group: TRequestStatusReasonGroup,
    label: string,
    source: AnalyticsSource = "page",
  ): Promise<TRequestStatusReason> {
    runInAction(() => {
      this.isMutating = true;
    });
    try {
      const created = await this.rootStore.requestReasonService.create(workspaceSlug, group, {
        label,
      });
      runInAction(() =>
        this.setReasons(workspaceSlug, group, [...this.getReasons(workspaceSlug, group), created]),
      );
      // Ids and the group only: the label is free text.
      track("request_reason_created", { reason_id: created.id, group, source });
      return created;
    } catch (error) {
      console.error("[request-reasons] create failed", { workspaceSlug, group, label, error });
      throw error;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  async updateReason(
    workspaceSlug: string,
    group: TRequestStatusReasonGroup,
    reasonId: string,
    data: { label?: string; is_active?: boolean },
    source: AnalyticsSource = "page",
  ): Promise<TRequestStatusReason | null> {
    const snapshot = this.getReasons(workspaceSlug, group);
    const before = snapshot.find((r) => r.id === reasonId);
    runInAction(() => {
      this.mutatingReasonId = reasonId;
      this.setReasons(
        workspaceSlug,
        group,
        snapshot.map((r) => (r.id === reasonId ? { ...r, ...data } : r)),
      );
    });
    try {
      const updated = await this.rootStore.requestReasonService.update(
        workspaceSlug,
        group,
        reasonId,
        data,
      );
      runInAction(() =>
        this.setReasons(
          workspaceSlug,
          group,
          this.getReasons(workspaceSlug, group).map((r) => (r.id === reasonId ? updated : r)),
        ),
      );
      this.trackReasonUpdate(reasonId, group, data, before, source);
      return updated;
    } catch (error) {
      // The server refuses a duplicate label and refuses retiring the last
      // active reason — both are 400s the editor surfaces, so the optimistic row
      // has to go back to what it was.
      runInAction(() => this.setReasons(workspaceSlug, group, snapshot));
      console.error("[request-reasons] update failed", {
        workspaceSlug,
        group,
        reasonId,
        data,
        error,
      });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingReasonId = null;
      });
    }
  }

  async deleteReason(
    workspaceSlug: string,
    group: TRequestStatusReasonGroup,
    reasonId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const snapshot = this.getReasons(workspaceSlug, group);
    runInAction(() => {
      this.mutatingReasonId = reasonId;
      this.setReasons(
        workspaceSlug,
        group,
        snapshot.filter((r) => r.id !== reasonId),
      );
    });
    try {
      await this.rootStore.requestReasonService.remove(workspaceSlug, group, reasonId);
      track("request_reason_deleted", { reason_id: reasonId, group, source });
    } catch (error) {
      runInAction(() => this.setReasons(workspaceSlug, group, snapshot));
      console.error("[request-reasons] delete failed", { workspaceSlug, group, reasonId, error });
      throw error;
    } finally {
      runInAction(() => {
        this.mutatingReasonId = null;
      });
    }
  }

  /** `from`/`to` index into the full list, so a caller filtering the view must
   *  translate before calling — the server renumbers the whole sequence. */
  async reorderReasons(
    workspaceSlug: string,
    group: TRequestStatusReasonGroup,
    from: number,
    to: number,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const current = this.getReasons(workspaceSlug, group);
    if (from === to || from < 0 || to < 0 || from >= current.length || to >= current.length) return;

    const next = [...current];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    runInAction(() => {
      this.setReasons(workspaceSlug, group, next);
      this.isMutating = true;
    });
    try {
      const persisted = await this.rootStore.requestReasonService.reorder(
        workspaceSlug,
        group,
        next.map((r) => r.id),
      );
      runInAction(() => this.setReasons(workspaceSlug, group, persisted));
      track("request_reasons_reordered", { reason_id: moved.id, group, source });
    } catch (error) {
      runInAction(() => this.setReasons(workspaceSlug, group, current));
      console.error("[request-reasons] reorder failed", { workspaceSlug, group, from, to, error });
      throw error;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  /** Retiring or reactivating a reason is its own lifecycle event. A rename in
   *  the same save also reports `request_reason_updated`, and only a value that
   *  changed counts. */
  private trackReasonUpdate(
    reasonId: string,
    group: TRequestStatusReasonGroup,
    data: { label?: string; is_active?: boolean },
    before: TRequestStatusReason | undefined,
    source: AnalyticsSource,
  ): void {
    if (data.is_active !== undefined && data.is_active !== before?.is_active) {
      track(data.is_active ? "request_reason_restored" : "request_reason_archived", {
        reason_id: reasonId,
        group,
        source,
      });
    }
    if (data.label !== undefined && data.label !== before?.label) {
      track("request_reason_updated", { reason_id: reasonId, group, fields: ["label"], source });
    }
  }
}
